import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { RequestOptions } from '../model/RequestOptions';
import { finalize, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { LoaderService } from './LoaderService';
import { NOTIFICATION_TYPE, NotificationService } from './NotificationService';
import { SessionService } from '../session-service.service';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  constructor(
    private httpService: HttpClient,
    @Inject('API_URL') private baseUrl: string,
    private translate: TranslateService,
    private loaderService: LoaderService,
    private notificationService: NotificationService,
    private sessionService: SessionService,
  ) {}

  private showLoaderAndHideOnCompletion<T>(
    observable: Observable<T>,
    requestOptions: RequestOptions
  ): Observable<T> {
    if (requestOptions.ShowLoader) {
      this.loaderService.show();
    }

    return observable
      .pipe(
        tap(
          (next) => {
            if (requestOptions.ShowSuccess) {
              let messageKey = requestOptions.IsDeleteAction
                ? 'Common.DeleteSuccessful'
                : 'Common.Successful';

              messageKey = this.translate.instant(messageKey);

              this.notificationService.notify({
                type: NOTIFICATION_TYPE.SUCCESS,
                messages: [messageKey]
              });
            }
          },
          (err) => {
            if (!requestOptions.HandleGenericError) {
              return;
            }

            let messageTitle = '';
            let messageBody = '';

            if (err.status === 422) {
              messageTitle = this.translate.instant(
                'Common.ValidationErrorTitle'
              );

              if (err.error.message) {
                messageBody = err.error.message
                  .split(',')
                  .map((err) => this.translate.instant('ErrorsEnum.' + err))
                  .join('; ');
              } else if (err.error.errors) {
                let errorMessages = err.error.errors.map((e) => e.messages);
                errorMessages = errorMessages.map(e => e.entry);
                errorMessages = errorMessages.flat();

                const currentLanguage = this.sessionService.getLocale() || this.translate.langs[0];
                errorMessages = errorMessages.filter(messageEntry => messageEntry.key === currentLanguage);

                errorMessages = errorMessages.map((msg) => msg.value);
                let messages = [...new Set(errorMessages)] as string[];

                messages = [...new Set(messages)] as string[];
                // ***

                this.notificationService.notify({
                  type: NOTIFICATION_TYPE.WARNING,
                  title: messageTitle,
                  messages,
                });
              }
            } else {
              messageTitle = this.translate.instant('Common.FailureTitle');

              messageBody = this.translate.instant('Common.FailureMessage');

              if (err.error && err.error.message) {
                const translatedErrorMessage = this.translate.instant(
                  'ErrorsEnum.' + err.error.message
                );

                if (!translatedErrorMessage.includes('ErrorsEnum.')) {
                  messageBody += ' -> ' + translatedErrorMessage;
                } else {
                  messageBody += ' -> ' + err.error.message;
                }

                const translatableParts = err.error.message.match(/\{(.*?)\}/);

                if (translatableParts) {
                  translatableParts.forEach((messageTemplate) => {
                    const messageTranslation = this.translate.instant(
                      `ErrorsEnum.${messageTemplate}`
                    );
                    messageBody = messageBody.replace(
                      messageTemplate,
                      messageTranslation
                    );
                  });
                }
              }

              this.notificationService.notify({
                type: NOTIFICATION_TYPE.ERROR,
                title: messageTitle,
                messages: [messageBody],
              });
            }
          }
        )
      )
      .pipe(
        finalize(() => {
          if (requestOptions.ShowLoader) {
            setTimeout(() => this.loaderService.hide(), 100);
          }
        })
      );
  }

  get<T>(
    url: string,
    query?: any,
    requestOptions?: RequestOptions
  ) {
    if (requestOptions === null || requestOptions === undefined) {
      requestOptions = new RequestOptions();
      requestOptions.ShowSuccess = false;
    }

    return this.showLoaderAndHideOnCompletion<T>(
      this.httpService.get<T>(this.baseUrl + url, {
        params: query,
      }),
      requestOptions
    );
  }

  getExternal<T>(
    url: string,
    query?: any,
    requestOptions?: RequestOptions
  ) {
    if (requestOptions === null || requestOptions === undefined) {
      requestOptions = new RequestOptions();
      requestOptions.ShowSuccess = false;
    }

    return this.showLoaderAndHideOnCompletion<T>(
      this.httpService.get<T>(url, {
        params: query,
      }),
      requestOptions
    );
  }

  post<T>(
    url: string,
    data: any,
    query?: any,
    requestOptions?: RequestOptions
  ) {
    if (requestOptions === null || requestOptions === undefined) {
      requestOptions = new RequestOptions();
    }

    const formedUrl = this.baseUrl + url;

    return this.showLoaderAndHideOnCompletion<T>(
      this.httpService.post<T>(formedUrl, data, {
        params: query,
        responseType:
          requestOptions.ResponseType === 'blob' ? ('blob' as 'json') : 'json',
      }),
      requestOptions
    );
  }

  delete<T>(
    url: string,
    query?: any,
    requestOptions?: RequestOptions
  ) {
    if (requestOptions === null || requestOptions === undefined) {
      requestOptions = new RequestOptions();
    }
    requestOptions.IsDeleteAction = true;

    return this.showLoaderAndHideOnCompletion<T>(
      this.httpService.delete<T>(this.baseUrl + url, {
        params: query,
      }),
      requestOptions
    );
  }
}
